import React, { useState, useEffect } from 'react';
import { GoogleLogin, googleLogout, CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import App from './App.tsx';
import { SESSION_EXPIRED_EVENT } from './Auth/sessionExpired.ts';
import { COMPTE_SUPPRIME_EVENT, jetonInvitationDeLAdresse, oublierChargementMoi, lireInvitationPublique, type LienInvitation } from './Auth/moi.ts';
import Confidentialite, { CHEMIN_CONFIDENTIALITE } from './Confidentialite/Confidentialite.tsx';

function Login() {
    const [user, setUser] = useState<any>(null);
    // Affiche un message explicatif quand on revient sur l'ecran de connexion
    // suite a une deconnexion forcee (403 du backend), plutot qu'une simple
    // reapparition silencieuse du bouton Google.
    const [sessionExpiree, setSessionExpiree] = useState<boolean>(false);
    // Lien d'invitation d'un coach (/rejoindre/<jeton>) : son nom est affiche
    // avant la connexion (undefined : pas de lien ou chargement ; null : lien invalide).
    const [invitation, setInvitation] = useState<LienInvitation | null | undefined>(undefined);

    useEffect(() => {
      const jeton = jetonInvitationDeLAdresse();
      if (jeton) lireInvitationPublique(jeton).then(setInvitation).catch(() => setInvitation(null));
    }, []);

    useEffect(() => {
      const storedUser = sessionStorage.getItem('user');
      if (storedUser) {
        console.log('Utilisateur connecté depuis la session :', JSON.parse(storedUser));
        setUser(JSON.parse(storedUser));
      }
    }, []);

    // Reconnexion SSO : declenchee par l'intercepteur fetch (voir
    // Auth/httpInterceptor.ts) quand le backend repond 403, c'est-a-dire
    // quand le jeton Google courant n'est plus valide.
    useEffect(() => {
      const surSessionExpiree = () => {
        console.log('Session expirée, retour à l\'écran de connexion Google.');
        oublierChargementMoi();
        setUser(null);
        setSessionExpiree(true);
      };
      window.addEventListener(SESSION_EXPIRED_EVENT, surSessionExpiree);
      return () => window.removeEventListener(SESSION_EXPIRED_EVENT, surSessionExpiree);
    }, []);

    // Compte supprime (onglet Zone de danger) : deconnexion complete.
    useEffect(() => {
      window.addEventListener(COMPTE_SUPPRIME_EVENT, handleLogout);
      return () => window.removeEventListener(COMPTE_SUPPRIME_EVENT, handleLogout);
    }, []);

  const handleSuccess: (credentialResponse: CredentialResponse) => void = (credentialResponse) => {
    const token = credentialResponse.credential;
    console.log('Jeton reçu :', token);
    sessionStorage.setItem('token', token || '');
    if (!token) {
      console.error('Aucun jeton reçu');
      return;
    }
    console.log('Utilisateur connecté');
    const userProfile = jwtDecode(token);
    oublierChargementMoi();
    setUser(userProfile);
    setSessionExpiree(false);
    sessionStorage.setItem('user', JSON.stringify(userProfile));
  };

  const handleError = () => {
    console.error('Échec de l\'authentification Google');
  };

  const handleLogout = () => {
    googleLogout();
    oublierChargementMoi();
    console.log('Utilisateur déconnecté');
    setUser(null);
    setSessionExpiree(false);
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
  };

  // La page d'information doit pouvoir etre lue avant de se connecter.
  if (!user && window.location.pathname === CHEMIN_CONFIDENTIALITE) {
    return <Confidentialite horsConnexion />;
  }

  return (
    <div>

      {!user ? (
        <div       style={{
        padding: '0px',
        margin: '0px',
        height: '100vh',
        width: '100vw',
        backgroundImage: `url(/background.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
>
          {/* Carte translucide sombre : garde la photo visible tout en
              assurant un contraste suffisant pour le titre et le message. */}
          <div style={{
            width: 'min(90vw, 440px)',
            marginBottom: '30vh',
            padding: '28px 32px',
            boxSizing: 'border-box',
            textAlign: 'center',
            color: '#fff',
            background: 'rgba(15, 20, 30, 0.65)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
            fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          }}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.5rem', fontWeight: 600 }}>
              Mes données de course
            </h2>
            {invitation ? (
              <p style={{ margin: '0 0 20px', fontSize: '1rem' }}>
                <b>{invitation.coach || 'Un coach'}</b> t'invite à le rejoindre comme athlète
                {invitation.club ? <> au club <b>{invitation.club}</b></> : null}.
                <br />
                <span style={{ opacity: 0.85 }}>Connecte-toi avec ton compte Google pour t'inscrire.</span>
              </p>
            ) : (
              <p style={{ margin: '0 0 20px', fontSize: '1rem', opacity: 0.85 }}>
                Connectez-vous pour accéder à votre espace.
              </p>
            )}
            {invitation === null && (
              <p style={{
                margin: '0 0 20px',
                padding: '10px 12px',
                borderRadius: '8px',
                background: 'rgba(255, 193, 7, 0.18)',
                border: '1px solid rgba(255, 193, 7, 0.6)',
                color: '#ffe08a',
              }}>
                Ce lien d'invitation n'est plus valide : demande un nouveau lien à ton coach.
              </p>
            )}
            {sessionExpiree && (
              <p style={{
                margin: '0 0 20px',
                padding: '10px 12px',
                borderRadius: '8px',
                background: 'rgba(255, 193, 7, 0.18)',
                border: '1px solid rgba(255, 193, 7, 0.6)',
                color: '#ffe08a',
                fontWeight: 600,
              }}>
                Votre session a expiré, merci de vous reconnecter.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GoogleLogin
              auto_select={false}
              onSuccess={handleSuccess}
              onError={handleError}
              //useOneTap
            />
            </div>
            <p style={{ margin: '20px 0 0', fontSize: '0.8rem', opacity: 0.75 }}>
              Aucun cookie publicitaire ni de mesure d'audience : seules les données nécessaires
              à la connexion sont conservées dans votre navigateur.{' '}
              <a href={CHEMIN_CONFIDENTIALITE} style={{ color: '#fff' }}>En savoir plus</a>
            </p>
          </div>
        </div>
      ) : (
        <div>
          <App user={user} handleLogout={handleLogout} />
        </div>
      )}
    </div>
  );
}

export default Login;
